/**
 * Category filter chips. `selectedCategory === undefined` means no filter —
 * the backend listing endpoint excludes Targeted by default in that mode, so
 * "All" surfaces Reference + Approved. Picking "Targeted" opts into the
 * derivative-image view (primarily for debugging).
 */
import React from 'react';
import type { ImageCategory } from '@/types/media';
import '@/styles/tooltip.css';

interface CategoryFilterProps {
  selectedCategory: ImageCategory | undefined;
  onChange: (category: ImageCategory | undefined) => void;
}

type Choice = {
  value: ImageCategory | undefined;
  label: string;
  tooltip?: string;
  tooltipAlign?: 'left' | 'right';
};

const CHOICES: Choice[] = [
  { value: undefined, label: 'All' },
  {
    value: 'reference',
    label: 'Reference',
    tooltip: 'Source images for Creative Studio (coming soon!). Not directly selectable for ad placements.',
    tooltipAlign: 'left',
  },
  {
    value: 'approved',
    label: 'Approved',
    tooltip: 'Ad-ready images. The only category that can be selected for ad placements.',
    tooltipAlign: 'left',
  },
  {
    value: 'targeted',
    label: 'Targeted',
    tooltip: 'Cropped derivatives of approved images, auto-resized for specific ad placements.',
    tooltipAlign: 'right',
  },
];

export function CategoryFilter({ selectedCategory, onChange }: CategoryFilterProps) {
  return (
    <div className="flex flex-col items-center gap-2 sm:flex-row sm:flex-wrap">
      <span className="text-sm text-gray-500 text-center">Category:</span>
      <div className="flex items-center gap-2 flex-wrap justify-center">
        {CHOICES.map((choice) => {
          const isSelected = selectedCategory === choice.value;
          const tooltipId = choice.tooltip ? `${choice.label.toLowerCase()}-chip-tooltip` : undefined;
          const chip = (
            <button
              type="button"
              onClick={() => onChange(choice.value)}
              aria-pressed={isSelected}
              aria-describedby={tooltipId}
              className={`
                inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
                transition-all cursor-pointer
                ${isSelected
                  ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {choice.label}
            </button>
          );
          return (
            <React.Fragment key={choice.label}>
              {choice.value === 'targeted' && (
                <span aria-hidden="true" className="h-4 w-px bg-gray-300 mx-1" />
              )}
              {choice.tooltip ? (
                <div className="tooltip-container">
                  {chip}
                  <span
                    id={tooltipId}
                    role="tooltip"
                    className={`tooltip-text tooltip-text-${choice.tooltipAlign ?? 'left'}`}
                    style={{ whiteSpace: 'normal', width: '22rem', maxWidth: '60vw', textAlign: 'left' }}
                  >
                    {choice.tooltip}
                  </span>
                </div>
              ) : (
                chip
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
